/**
 * Lead Sheets → Einstellungen → Speicher: Wie viel deiner Schnittstelle
 * Rot Cloud (500 MB je Konto) die Band-Projekte belegen – als
 * Kreisdiagramm mit Prozenten, je Projekt mit deinem Anteil.
 *
 * Aendern der Anteile: „Aufteilen …“ oeffnet das Teilen-Fenster des
 * Projekts (nur der Ersteller, und erst wenn die Freigaben eingerichtet
 * sind – vorher traegt der Ersteller alles).
 */
import type { Project } from '../../types.ts';
import { h } from '../../ui/dom.ts';
import { storageDonut } from '../../ui/storage-donut.ts';
import { sharingApi, sharingApiAny } from '../../platform/sharing-api.ts';
import { contribution, normalizeShares } from '../../firebase/sharing.ts';
import { listProjects } from '../../db/repo.ts';

export async function storageCard(onChanged: () => void): Promise<HTMLElement> {
  const card = h('section', { class: 'card' }, h('h2', { class: 'card__title', text: 'Speicher' }));
  const api = sharingApiAny();
  if (!api) {
    card.append(h('p', { class: 'hint', text: 'Die Aufteilung des Speichers gibt es mit Cloud-Anmeldung. Ohne Anmeldung liegt alles nur auf diesem Gerät.' }));
    return card;
  }
  const projects: Project[] = await listProjects();
  const me = api.me;
  const rows = projects
    .map((p) => {
      const ids = (p.members?.length ? p.members : [{ id: me, role: 'owner' as const, name: '' }]).map((m) => m.id);
      const owner = p.members?.find((m) => m.role === 'owner')?.id ?? me;
      const shares = normalizeShares(p.storageShares, ids, owner);
      const bytes = api.projectBytes(p.id);
      return { p, bytes, mine: contribution(bytes, shares, me), pct: Math.round(shares[me] ?? 0), owner };
    })
    .filter((r) => r.mine > 0 || r.bytes > 0)
    .sort((a, b) => b.mine - a.mine);

  card.append(
    storageDonut({
      title: 'Deine Schnittstelle Rot Cloud',
      total: api.quotaBytes,
      parts: rows.map((r) => ({ label: r.p.name || 'Ohne Namen', bytes: r.mine, note: r.pct < 100 ? `dein Anteil: ${r.pct} % von ${(r.bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : 'trägst du allein' })),
      empty: 'Noch keine Band-Projekte in der Cloud.',
    }),
  );

  const enabled = !!sharingApi();
  if (rows.length) {
    card.append(
      h(
        'div',
        { class: 'storage-projects' },
        ...rows.map((r) =>
          h(
            'div',
            { class: 'storage-projects__row' },
            h('span', { class: 'storage-projects__name', text: r.p.name || 'Ohne Namen' }),
            h('span', { class: 'hint', text: `${(r.bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB gesamt` }),
            enabled && r.owner === me
              ? h('button', {
                  class: 'btn btn--small',
                  text: 'Aufteilen …',
                  attrs: { type: 'button' },
                  on: { click: () => void import('./share-dialog.ts').then((m) => m.openShareDialog(r.p, onChanged)) },
                })
              : null,
          ),
        ),
      ),
    );
  }
  card.append(
    h('p', {
      class: 'hint',
      text: enabled
        ? 'Wer ein Projekt angelegt hat, teilt den Speicher unter den Mitgliedern auf („Aufteilen …“). Jeder sieht hier seinen Anteil.'
        : 'Aufteilen unter Band-Mitgliedern geht, sobald die Freigaben eingerichtet sind (Verwaltung → Wartung & Backups → Freigaben einrichten). Bis dahin trägt der Ersteller den Speicher allein.',
    }),
  );
  return card;
}
